import React, { useState, useEffect, useRef } from 'react';
import {
  Cloud, Calendar, DollarSign, FileText, BarChart3, Stethoscope, Smartphone,
  Pill, Brain, Users, Video, MessageCircle, BookOpen, Award, Settings,
  CheckCircle, Network, Zap, Shield, Star, TrendingUp, ArrowRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TechnologyEcosystemEnhanced = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVisible, setIsVisible] = useState({});
  const [counters, setCounters] = useState({ clients: 0, uptime: 0, providers: 0 });
  const observerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animated counter effect
  useEffect(() => {
    const animateCounter = (key, target, duration = 2000) => {
      const start = 0;
      const increment = target / (duration / 16);
      let current = start;

      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCounters(prev => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
        }
      }, 16);
    };

    const timeout = setTimeout(() => {
      animateCounter('clients', 500);
      animateCounter('uptime', 99);
      animateCounter('providers', 10000);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    document.querySelectorAll('[data-animate]').forEach(el => {
      if (observerRef.current) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const section1Features = [
    { icon: Calendar, title: 'Practice Management System (PMS)', description: 'Centralized command center for scheduling, registration, and workflow.', color: '#1D57D5', stat: '40% Efficiency' },
    { icon: DollarSign, title: 'Revenue Cycle Management (RCM)', description: 'End-to-end financial tracking, claim to reimbursement.', color: '#1D57D5', stat: '25% Revenue ↑' },
    { icon: FileText, title: 'Intelligent Billing & Claims Engine', description: 'Automated coding and claims scrubbing for faster payments.', color: '#1D57D5', stat: '60% Faster' },
    { icon: BarChart3, title: 'Provider Performance Analytics', description: 'Track key metrics: retention, utilization, and revenue.', color: '#1D57D5', stat: 'Real-time Data' }
  ];

  const section2Features = [
    { icon: Smartphone, title: 'Independent Provider Suite', description: 'Lightweight, mobile-first interface for private practice.', color: '#F4CE7B' },
    { icon: Pill, title: 'e-Prescribing (eRx) Module', description: 'Secure, instant digital prescriptions with automatic checks.', color: '#F4CE7B' },
    { icon: Brain, title: 'Clinical Decision Support System (CDSS)', description: 'Evidence-based guidelines integrated into workflow.', color: '#F4CE7B' }
  ];

  const section3Features = [
    { icon: Video, title: 'Patient Engagement App', description: 'Mobile app for consults, booking, and health records.', color: '#1D57D5' },
    { icon: MessageCircle, title: '"Health Genie" AI Assistant', description: 'AI-Powered Health Companion for symptom triage and queries.', color: '#1D57D5' }
  ];

  const section4Features = [
    { icon: Award, title: 'Medical Learning Management System (LMS)', description: 'Dedicated hub for CME, certifications, and protocol training.', color: '#F4CE7B' },
    { icon: Network, title: 'Community Knowledge Exchange', description: 'Peer-to-peer forum for case studies and second opinions.', color: '#F4CE7B' }
  ];

  return (
    <>
      <Navbar />

      {/* Enhanced Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 50%, #1a4bb8 100%)',
        padding: isMobile ? '100px 20px 60px' : '120px 40px 100px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated Background Grid */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.15) 1px, transparent 0)',
          backgroundSize: '48px 48px',
          animation: 'gridMove 20s linear infinite'
        }} />

        {/* Floating Orbs */}
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: `${80 + i * 20}px`,
            height: `${80 + i * 20}px`,
            background: `radial-gradient(circle, ${i % 2 === 0 ? 'rgba(244, 206, 123, 0.15)' : 'rgba(255, 255, 255, 0.1)'} 0%, transparent 70%)`,
            borderRadius: '50%',
            top: `${10 + i * 15}%`,
            left: `${5 + i * 15}%`,
            animation: `float ${6 + i}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`
          }} />
        ))}

        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Premium Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(244, 206, 123, 0.2)',
            border: '2px solid rgba(244, 206, 123, 0.5)',
            borderRadius: '50px',
            padding: '12px 28px',
            marginBottom: '28px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(244, 206, 123, 0.2)',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            <Zap size={20} color="#F4CE7B" />
            <span style={{
              color: '#F4CE7B',
              fontSize: '14px',
              fontWeight: '700',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>GEWO Technology Platform</span>
            <Star size={16} color="#F4CE7B" fill="#F4CE7B" />
          </div>

          {/* Main Heading */}
          <h1 style={{
            fontSize: isMobile ? '36px' : '64px',
            fontWeight: '900',
            color: 'white',
            marginBottom: '24px',
            letterSpacing: isMobile ? '-0.5px' : '-1.5px',
            lineHeight: '1.1',
            animation: 'fadeInUp 0.8s ease-out 0.2s backwards'
          }}>
            Our Integrated Technology{' '}
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 50%, #FFF4E3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              position: 'relative',
              display: 'inline-block'
            }}>
              Ecosystem
              {/* Glow effect */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '120%',
                height: '120%',
                background: 'radial-gradient(circle, rgba(244, 206, 123, 0.3) 0%, transparent 70%)',
                filter: 'blur(20px)',
                zIndex: -1,
                animation: 'pulse 3s ease-in-out infinite'
              }} />
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: isMobile ? '16px' : '22px',
            color: 'rgba(255, 255, 255, 0.95)',
            maxWidth: '750px',
            margin: isMobile ? '0 auto 32px' : '0 auto 48px',
            lineHeight: '1.7',
            fontWeight: '400',
            animation: 'fadeInUp 1s ease-out 0.4s backwards'
          }}>
            Empowering <strong>Administrators</strong>, <strong>Providers</strong>, and <strong>Patients</strong> with a Unified Cloud Platform
          </p>

          {/* Animated Stats Bar */}
          <div style={{
            display: 'flex',
            gap: '32px',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: '40px',
            animation: 'fadeInUp 1.2s ease-out 0.6s backwards'
          }}>
            <div style={{
              padding: '20px 32px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{counters.clients}+</div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '600', letterSpacing: '0.5px', marginTop: '4px' }}>Healthcare Clients</div>
            </div>

            <div style={{
              padding: '20px 32px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{counters.uptime}.9%</div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '600', letterSpacing: '0.5px', marginTop: '4px' }}>Platform Uptime</div>
            </div>

            <div style={{
              padding: '20px 32px',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                fontSize: '36px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{counters.providers.toLocaleString()}+</div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '600', letterSpacing: '0.5px', marginTop: '4px' }}>Active Providers</div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex',
            gap: '40px',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            animation: 'fadeInUp 1.4s ease-out 0.8s backwards'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle size={24} color="#43e97b" />
              <span style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>All-in-One Platform</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CheckCircle size={24} color="#F4CE7B" />
              <span style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>Cloud-Based Infrastructure</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={24} color="#43e97b" />
              <span style={{ color: 'white', fontSize: '16px', fontWeight: '600' }}>HIPAA Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Clinic & Operations Cloud */}
      <section id="section1" data-animate style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: 'white',
        opacity: isVisible.section1 ? 1 : 0,
        transform: isVisible.section1 ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            {/* Enhanced Large Icon */}
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '32px',
              background: 'linear-gradient(135deg, #1D57D5 0%, #4facfe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              boxShadow: '0 20px 60px rgba(29, 87, 213, 0.4), 0 0 0 1px rgba(29, 87, 213, 0.1)',
              position: 'relative'
            }}>
              <Cloud size={70} color="white" />
              {/* Pulse ring */}
              <div style={{
                position: 'absolute',
                inset: '-10px',
                borderRadius: '38px',
                border: '2px solid rgba(29, 87, 213, 0.3)',
                animation: 'pulseRing 2s ease-out infinite'
              }} />
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
              border: '2px solid #1D57D5',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: '#1D57D5',
                fontSize: '16px',
                fontWeight: '800',
                letterSpacing: '0.8px'
              }}>1. CLINIC & OPERATIONS CLOUD</span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '32px' : '42px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              For Organization{' '}
              <span style={{
                background: 'linear-gradient(135deg, #1D57D5 0%, #4facfe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Administrators</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              Comprehensive management tools for healthcare operations excellence
            </p>
          </div>

          {/* Enhanced Feature Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {section1Features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    borderRadius: '24px',
                    padding: '36px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(29, 87, 213, 0.2), 0 0 40px rgba(29, 87, 213, 0.1)';
                    e.currentTarget.style.backgroundImage = `linear-gradient(white, white), linear-gradient(135deg, ${feature.color} 0%, #4facfe 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundImage = 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)';
                  }}
                >
                  {/* Shimmer effect on hover */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                    transition: 'left 0.6s ease'
                  }} className="shimmer" />

                  {/* Stat Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    padding: '6px 12px',
                    background: `${feature.color}15`,
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: '700',
                    color: feature.color,
                    border: `1px solid ${feature.color}30`
                  }}>
                    {feature.stat}
                  </div>

                  {/* Icon with Gradient Background */}
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '18px',
                    background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}CC 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    boxShadow: `0 8px 32px ${feature.color}40`,
                    position: 'relative',
                    transition: 'all 0.4s ease'
                  }}>
                    <Icon size={36} color="white" />
                    {/* Glow ring */}
                    <div style={{
                      position: 'absolute',
                      inset: '-4px',
                      borderRadius: '22px',
                      background: `linear-gradient(135deg, ${feature.color}40, transparent)`,
                      filter: 'blur(8px)',
                      zIndex: -1
                    }} />
                  </div>

                  <h3 style={{
                    fontSize: '21px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  <p style={{
                    fontSize: '15px',
                    color: '#64748B',
                    lineHeight: '1.7',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>

                  {/* Arrow icon */}
                  <div style={{
                    marginTop: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: feature.color,
                    fontSize: '14px',
                    fontWeight: '600',
                    opacity: 0,
                    transition: 'opacity 0.3s ease'
                  }} className="learn-more">
                    Learn more <ArrowRight size={16} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, #1D57D5 20%, #F4CE7B 50%, #1D57D5 80%, transparent 100%)',
        opacity: 0.3
      }} />

      {/* Section 2: Provider Workstation */}
      <section id="section2" data-animate style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        opacity: isVisible.section2 ? 1 : 0,
        transform: isVisible.section2 ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out 0.2s'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '32px',
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              boxShadow: '0 20px 60px rgba(244, 206, 123, 0.4)',
              position: 'relative'
            }}>
              <Stethoscope size={70} color="white" />
              <div style={{
                position: 'absolute',
                inset: '-10px',
                borderRadius: '38px',
                border: '2px solid rgba(244, 206, 123, 0.3)',
                animation: 'pulseRing 2s ease-out infinite 0.5s'
              }} />
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: '#D4A84E',
                fontSize: '16px',
                fontWeight: '800',
                letterSpacing: '0.8px'
              }}>2. PROVIDER WORKSTATION</span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '32px' : '42px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              For Doctors &{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Practitioners</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              Professional tools designed for clinical excellence and efficiency
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {section2Features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #FFF4E3 0%, #FFE4C4 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    borderRadius: '24px',
                    padding: '44px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(244, 206, 123, 0.25), 0 0 40px rgba(244, 206, 123, 0.15)';
                    e.currentTarget.style.backgroundImage = `linear-gradient(white, white), linear-gradient(135deg, ${feature.color} 0%, #FFD98E 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundImage = 'linear-gradient(white, white), linear-gradient(135deg, #FFF4E3 0%, #FFE4C4 100%)';
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${feature.color} 0%, #FFD98E 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '28px',
                    boxShadow: `0 12px 40px ${feature.color}50`,
                    position: 'relative'
                  }}>
                    <Icon size={40} color="white" />
                    <div style={{
                      position: 'absolute',
                      inset: '-6px',
                      borderRadius: '26px',
                      background: `linear-gradient(135deg, ${feature.color}30, transparent)`,
                      filter: 'blur(10px)',
                      zIndex: -1
                    }} />
                  </div>

                  <h3 style={{
                    fontSize: '23px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '14px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  <p style={{
                    fontSize: '16px',
                    color: '#64748B',
                    lineHeight: '1.7',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, #F4CE7B 20%, #1D57D5 50%, #F4CE7B 80%, transparent 100%)',
        opacity: 0.3
      }} />

      {/* Section 3: Patient Experience Portal */}
      <section id="section3" data-animate style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: 'white',
        opacity: isVisible.section3 ? 1 : 0,
        transform: isVisible.section3 ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out 0.2s'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '32px',
              background: 'linear-gradient(135deg, #1D57D5 0%, #4facfe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              boxShadow: '0 20px 60px rgba(29, 87, 213, 0.4)',
              position: 'relative'
            }}>
              <Users size={70} color="white" />
              <div style={{
                position: 'absolute',
                inset: '-10px',
                borderRadius: '38px',
                border: '2px solid rgba(29, 87, 213, 0.3)',
                animation: 'pulseRing 2s ease-out infinite 1s'
              }} />
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #E3EDFF 0%, #F0F6FF 100%)',
              border: '2px solid #1D57D5',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: '#1D57D5',
                fontSize: '16px',
                fontWeight: '800',
                letterSpacing: '0.8px'
              }}>3. PATIENT EXPERIENCE PORTAL</span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '32px' : '42px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              For Patients &{' '}
              <span style={{
                background: 'linear-gradient(135deg, #1D57D5 0%, #4facfe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>End-Users</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              Empowering patients with accessible, modern healthcare technology
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(420px, 1fr))',
            gap: '40px',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {section3Features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    borderRadius: '28px',
                    padding: isMobile ? '28px' : '52px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.15}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 24px 70px rgba(29, 87, 213, 0.2), 0 0 50px rgba(29, 87, 213, 0.1)';
                    e.currentTarget.style.backgroundImage = `linear-gradient(white, white), linear-gradient(135deg, ${feature.color} 0%, #4facfe 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundImage = 'linear-gradient(white, white), linear-gradient(135deg, #E2E8F0 0%, #CBD5E1 100%)';
                  }}
                >
                  <div style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, ${feature.color} 0%, #4facfe 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '28px',
                    boxShadow: `0 16px 48px ${feature.color}50`,
                    position: 'relative'
                  }}>
                    <Icon size={45} color="white" />
                    <div style={{
                      position: 'absolute',
                      inset: '-8px',
                      borderRadius: '32px',
                      background: `linear-gradient(135deg, ${feature.color}30, transparent)`,
                      filter: 'blur(12px)',
                      zIndex: -1
                    }} />
                  </div>

                  <h3 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '16px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  <p style={{
                    fontSize: '17px',
                    color: '#64748B',
                    lineHeight: '1.8',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div style={{
        height: '2px',
        background: 'linear-gradient(90deg, transparent 0%, #1D57D5 20%, #F4CE7B 50%, #1D57D5 80%, transparent 100%)',
        opacity: 0.3
      }} />

      {/* Section 4: Education & Growth Hub */}
      <section id="section4" data-animate style={{
        padding: isMobile ? '60px 20px' : '120px 40px',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)',
        opacity: isVisible.section4 ? 1 : 0,
        transform: isVisible.section4 ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out 0.2s',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle background pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(#F4CE7B 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          opacity: 0.1,
          zIndex: 0
        }} />

        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '70px' }}>
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '32px',
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 28px',
              boxShadow: '0 20px 60px rgba(244, 206, 123, 0.4)',
              position: 'relative'
            }}>
              <BookOpen size={70} color="white" />
              <div style={{
                position: 'absolute',
                inset: '-10px',
                borderRadius: '38px',
                border: '2px solid rgba(244, 206, 123, 0.3)',
                animation: 'pulseRing 2s ease-out infinite 1.5s'
              }} />
            </div>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
              border: '2px solid #F4CE7B',
              borderRadius: '50px',
              padding: '10px 24px',
              marginBottom: '20px'
            }}>
              <span style={{
                color: '#D4A84E',
                fontSize: '16px',
                fontWeight: '800',
                letterSpacing: '0.8px'
              }}>4. EDUCATION & GROWTH HUB</span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '32px' : '42px',
              fontWeight: '800',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-0.5px'
            }}>
              Continuous Learning &{' '}
              <span style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Community</span>
            </h2>

            <p style={{
              fontSize: '19px',
              color: '#64748B',
              maxWidth: '650px',
              margin: '0 auto'
            }}>
              Professional development and collaborative knowledge sharing
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(420px, 1fr))',
            gap: '40px',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {section4Features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid transparent',
                    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #FFF9E6 0%, #FFEDD5 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                    borderRadius: '28px',
                    padding: isMobile ? '28px' : '52px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    animation: `fadeInUp 0.6s ease-out ${index * 0.15}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 24px 70px rgba(244, 206, 123, 0.25), 0 0 50px rgba(244, 206, 123, 0.15)';
                    e.currentTarget.style.backgroundImage = `linear-gradient(white, white), linear-gradient(135deg, ${feature.color} 0%, #FFD98E 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.backgroundImage = 'linear-gradient(white, white), linear-gradient(135deg, #FFF4E3 0%, #FFE4C4 100%)';
                  }}
                >
                  <div style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, ${feature.color} 0%, #FFD98E 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '28px',
                    boxShadow: `0 16px 48px ${feature.color}50`,
                    position: 'relative'
                  }}>
                    <Icon size={45} color="white" />
                    <div style={{
                      position: 'absolute',
                      inset: '-8px',
                      borderRadius: '32px',
                      background: `linear-gradient(135deg, ${feature.color}30, transparent)`,
                      filter: 'blur(12px)',
                      zIndex: -1
                    }} />
                  </div>

                  <h3 style={{
                    fontSize: '26px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '16px',
                    lineHeight: '1.3'
                  }}>
                    {feature.title}
                  </h3>

                  <p style={{
                    fontSize: '17px',
                    color: '#64748B',
                    lineHeight: '1.8',
                    margin: 0
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enhanced Call to Action */}
      <section style={{
        padding: '120px 40px',
        background: 'linear-gradient(135deg, #1D57D5 0%, #133E97 50%, #1a4bb8 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.1) 1px, transparent 0)',
          backgroundSize: '40px 40px',
          animation: 'gridMove 15s linear infinite'
        }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Premium badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(244, 206, 123, 0.2)',
            border: '2px solid rgba(244, 206, 123, 0.5)',
            borderRadius: '50px',
            padding: '10px 24px',
            marginBottom: '28px',
            backdropFilter: 'blur(10px)'
          }}>
            <Zap size={18} color="#F4CE7B" />
            <span style={{
              color: '#F4CE7B',
              fontSize: '13px',
              fontWeight: '700',
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>Enterprise Solutions</span>
          </div>

          <h2 style={{
            fontSize: '52px',
            fontWeight: '900',
            color: 'white',
            marginBottom: '24px',
            letterSpacing: '-1px',
            lineHeight: '1.1'
          }}>
            Ready to Transform Your{' '}
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 50%, #FFF4E3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Healthcare Operations?
            </span>
          </h2>

          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.95)',
            marginBottom: '48px',
            lineHeight: '1.7',
            maxWidth: '700px',
            margin: '0 auto 48px'
          }}>
            Join <strong>500+ leading healthcare organizations</strong> using GEWO's integrated ecosystem
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '40px'
          }}>
            <button style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              color: '#133E97',
              fontSize: '18px',
              fontWeight: '700',
              padding: '20px 48px',
              borderRadius: '50px',
              border: 'none',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 8px 32px rgba(244, 206, 123, 0.5)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 16px 48px rgba(244, 206, 123, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(244, 206, 123, 0.5)';
              }}>
              Schedule a Demo
              <ArrowRight size={20} />
            </button>

            <button style={{
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '18px',
              fontWeight: '700',
              padding: '20px 48px',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}>
              View Pricing
            </button>
          </div>

          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            <CheckCircle size={16} style={{ display: 'inline', marginRight: '6px' }} />
            No credit card required • Free 30-day trial • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />

      {/* Enhanced Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          33% {
            transform: translateY(-20px) translateX(10px);
          }
          66% {
            transform: translateY(-10px) translateX(-10px);
          }
        }

        @keyframes gridMove {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(48px);
          }
        }

        @keyframes pulseRing {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          100% {
            opacity: 0;
            transform: scale(1.3);
          }
        }

        div:hover .shimmer {
          left: 100%;
        }

        div:hover .learn-more {
          opacity: 1 !important;
        }

        @media (max-width: 768px) {
          h1 {
            font-size: 42px !important;
          }
          h2 {
            font-size: 32px !important;
          }
          section {
            padding: 80px 24px !important;
          }
        }
      `}</style>
    </>
  );
};

export default TechnologyEcosystemEnhanced;